import { useEffect, useRef, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/cn";
import { Button } from "./Button";
import { FOCUSABLE_SELECTOR, getTrapWrapTarget } from "~/lib/focus-trap";
import type { DashboardRitualDataAttr } from "~/lib/dashboard-ritual-theme";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  className?: string;
  /** Merged onto the fixed full-viewport stack (e.g. `z-[9000]` above the main nav). */
  stackClassName?: string;
  children: React.ReactNode;
  /** Default centered card; bottom sheet on small screens when set. */
  variant?: "center" | "bottomSheet";
  showCloseButton?: boolean;
  /** Accessible name for the dialog (a11y — aria-dialog-name). */
  ariaLabel?: string;
  /**
   * When portaling to `document.body`, ritual CSS variables are otherwise
   * missing — set to match the dashboard `data-ritual`. Same escape hatch
   * `BottomSheet` has carried since it was written; `Modal` lacked it, and the
   * first surface to portal ritual-themed content through it (the T407 calendar
   * day sheet) shipped a transparent panel with invisible ink because
   * `--ritual-bg` / `--ritual-ink` / `--ritual-ghost` resolved to nothing, and
   * no selection ring because `color-mix()` with an undefined var drops the
   * whole declaration.
   */
  ritualTheme?: DashboardRitualDataAttr;
}

export function Modal({
  open,
  onClose,
  className,
  stackClassName,
  children,
  variant = "center",
  showCloseButton = true,
  ariaLabel,
  ritualTheme,
}: ModalProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  // Portal target only exists on the client. Mount-gating also keeps the
  // overlay out of SSR markup (it is always client-driven via `open`).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // The element focused before the modal opened, so focus can be restored on
  // close (a11y — never strand the keyboard user at the top of the page).
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  // Read `onClose` through a ref so the keydown handler stays referentially
  // stable: otherwise the focus-management effect below would re-run (and steal
  // focus back to the first element) on every parent re-render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onCloseRef.current();
      return;
    }
    if (e.key === "Tab" && panelRef.current) {
      const focusables = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      // getTrapWrapTarget returns null when focus is already correctly trapped
      // (mid-list, or a sole focusable already focused). For the sole-focusable
      // case we must still preventDefault so focus can't escape — without
      // re-focusing it (that flashes the ring). See R-T180-1.
      const target = getTrapWrapTarget(focusables, document.activeElement, e.shiftKey);
      const activeIsTrapped =
        focusables.length === 1 && focusables[0] === document.activeElement;
      if (target) {
        e.preventDefault();
        target.focus();
      } else if (activeIsTrapped) {
        e.preventDefault();
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    // Move focus into the dialog once the portal has painted. Falls back to the
    // panel itself (tabIndex=-1) when it holds no focusable children.
    const raf = requestAnimationFrame(() => {
      const root = panelRef.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? root).focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      restoreFocusRef.current?.focus?.();
    };
  }, [open, handleKeyDown]);

  if (!open || !mounted) return null;

  const isSheet = variant === "bottomSheet";

  // Portal to <body> so the fixed overlay is viewport-relative and never
  // confined by a transformed/contained ancestor (e.g. the profile shell).
  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[100] flex animate-[fadeIn_0.2s_ease-out]",
        isSheet
          ? "items-end justify-center bg-[rgba(10,9,8,0.48)] p-0 pt-10 sm:items-center sm:p-4 sm:backdrop-blur-[2px]"
          : "items-center justify-center p-4 backdrop-blur-sm bg-darkBg/80",
        stackClassName,
      )}
      onClick={(e) => {
        if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      {...(ritualTheme !== undefined ? { "data-ritual": ritualTheme } : {})}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "relative w-full border border-borderLight",
          isSheet
            ? "flex max-h-[min(88vh,880px)] min-h-0 flex-col overflow-hidden rounded-t-[14px] border-[rgba(10,9,8,0.08)] bg-[var(--color-primary)] p-0 shadow-[0_-8px_40px_rgba(0,0,0,0.12)] motion-safe:animate-[tfc-sheet-up_0.34s_cubic-bezier(0.22,1,0.36,1)_both] sm:max-h-[90vh] sm:max-w-lg sm:rounded-sm sm:shadow-none"
            : "bg-primary p-8 md:p-12 max-w-lg animate-[fadeIn_0.2s_ease-out]",
          className,
        )}
      >
        {showCloseButton ? (
          <Button
            variant="unstyled"
            type="button"
            onClick={onClose}
            className={cn(
              "absolute z-[1] text-2xl font-light text-[var(--color-textMuted)] transition-opacity hover:opacity-50",
              "min-h-11 min-w-11",
              isSheet ? "right-[10px] top-[10px]" : "top-4 right-4",
            )}
            aria-label={t("ui.close")}
          >
            &times;
          </Button>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}
